/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import {
    ArrowDownloadFilled,
    ArrowDownloadRegular,
    bundleIcon,
    DeleteFilled,
    DeleteRegular,
    InfoFilled,
    InfoRegular,
    RenameFilled,
    RenameRegular,
} from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { Column, Row } from '../../../common';
import { useFileIcons } from '../../../common/useFileIcons';
import { useUtilities } from '../../../../utilities/useUtilities';
import {
    CustomDataTable,
    CustomDataTableAction,
    CustomDataTableColumn,
    CustomDataTableProps,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { RenameDocDialog } from '../../../dialogs/RenameDocDialog';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { useDocumentDownload } from '../../../../data/services/useDocumentDownload';
import { CaseDocument, UserAccount } from '../../../../data/types';
import { Text } from '@fluentui/react-components';
import { useCaseMilestoneState } from '../../../../redux/reducers/caseMilestoneReducer';
import { useCaseTaskState } from '../../../../redux/reducers/caseTaskReducer';
import { useCaseEventState } from '../../../../redux/reducers/caseEventReducer';

enum ColumnName {
    name = 'name',
    descrip = 'description',
    modified = 'modified',
    milestone = 'milestone',
    task = 'task',
    event = 'event',
    size = 'size',
    actions = 'actions',
}

type DocumentTableProps = Partial<CustomDataTableProps<CaseDocument>> & {
    docs: CaseDocument[];
    singleSelect?: boolean;
    onSelectionChanged?: (docs: CaseDocument[]) => void;
    setDrawerOpen?: (open: boolean) => void;
    onDeleteDocs?: (docs: CaseDocument[]) => Promise<void>;
    onModifyDocument?: (doc: CaseDocument) => Promise<void>;
    showMilestone?: boolean;
    showTask?: boolean;
    showEvent?: boolean;
};
export const DocumentTable = (props: DocumentTableProps) => {
    const { docs, singleSelect, onSelectionChanged, setDrawerOpen, onDeleteDocs, onModifyDocument, showMilestone = false, showTask = false, showEvent = false, ...otherProps } = props;

    const msState = useCaseMilestoneState();
    const taskState = useCaseTaskState();
    const eventState = useCaseEventState();

    //Hooks
    const { primaryAccount } = useAccountState();
    const { accountUsers } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(primaryAccount?.user);
    const { getFileIcon } = useFileIcons();
    const utils = useUtilities();
    const { downloadDocAsync } = useDocumentDownload();

    //State
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [renameOpen, setRenameOpen] = useState<boolean>(false);
    const [actionDoc, setActionDoc] = useState<CaseDocument>();

    //Logic
    const clickDeleteDocs = async (docs: CaseDocument[]) => {
        !!onDeleteDocs && (await onDeleteDocs(docs));

        onSelectionChanged && onSelectionChanged([]);
    };
    const getMilestone = (milestoneId: string) => {
        return msState.milestones.find((m) => m.milestoneId === milestoneId);
    }
    const getTask = (taskId: string) => {
        return taskState.tasks.find((t) => t.taskId === taskId);
    }
    const getEvent = (eventId: string) => {
        return eventState.events.find((e) => e.eventId === eventId);
    }

    //Table Setup
    const columns: CustomDataTableColumn<CaseDocument>[] = [
        {
            id: ColumnName.name,
            compare: (a, b) => {
                return a.name?.localeCompare(b.name || '') || 0;
            },
            header: 'Name',
            renderCell: (doc) => {
                return (
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                {getFileIcon(doc.name as string)}
                            </Column>
                            <Column>{doc.name}</Column>
                        </Row>
                );
            },
            cellFocusMode: 'group',
            defaultWidth: 300,
            minWidth: 100,
        },
        {
            id: ColumnName.descrip,
            compare: (a, b) => {
                return a.description?.localeCompare(b.description || '') || 0;
            },
            header: 'Description',
            renderCell: (doc) => {
                return (
                        <Text>{doc?.description}</Text>
                );
            },
            defaultWidth: 300,
            minWidth: 100,
        },
        {
            id: ColumnName.milestone,
            compare: (a, b) => {
                const aMs = getMilestone(a.milestoneId || '');
                const bMs = getMilestone(b.milestoneId || '');
                return aMs?.name?.localeCompare(bMs?.name || '') || 0;
            },
            header: 'Milestone',
            renderCell: (doc) => {
                return (
                        <Text>{getMilestone(doc.milestoneId || '')?.name}</Text>
                );
            },
            show: false, //showMilestone,
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.task,
            compare: (a, b) => {
                const aTask = getTask(a.taskId || '');
                const bTask = getTask(b.taskId || '');
                return aTask?.title?.localeCompare(bTask?.title || '') || 0;
            },
            header: 'Task',
            renderCell: (doc) => {
                return (
                        <Text>{getTask(doc.taskId || '')?.title}</Text>
                );
            },
            show: false, //showTask,
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.event,
            compare: (a, b) => {
                const aEvent = getEvent(a.eventId || '');
                const bEvent = getEvent(b.eventId || '');
                return aEvent?.event?.localeCompare(bEvent?.event || '') || 0;
            },
            header: 'Event',
            renderCell: (doc) => {
                return (
                        <Text>{getEvent(doc.eventId || '')?.event}</Text>
                );
            },
            show: false, //showEvent,
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.modified,
            compare: (a, b) => {
                const aMod = new Date(a.lifecycle?.modifiedOn || 0).valueOf();
                const aCreate = new Date(a.lifecycle?.createdOn || 0).valueOf();

                const bMod = new Date(b.lifecycle?.modifiedOn || 0).valueOf();
                const bCreate = new Date(b.lifecycle?.createdOn || 0).valueOf();

                const aVal = Math.max(aMod, aCreate);
                const bVal = Math.max(bMod, bCreate);
                return aVal - bVal;
            },
            header: 'Modified On',
            renderCell: (doc) => {
                const docMod = new Date(doc.lifecycle?.modifiedOn || 0).valueOf();
                const docCreate = new Date(doc.lifecycle?.createdOn || 0).valueOf();

                const theDate =
                    docMod > docCreate ? new Date(doc.lifecycle?.modifiedOn || 0) : new Date(doc.lifecycle?.createdOn || 0);
                const theUser = docMod > docCreate ? doc.lifecycle?.modifiedBy : doc.lifecycle?.createdBy;

                const dateVal = new Date(theDate);

                return (
                        <Column noGap>
                            <Text>{dateVal.toLocaleDateString()}</Text>
                            <Text>{utils.getUsersName(theUser as string, accountUsers)}</Text>
                        </Column>
                );
            },
            defaultWidth: 120,
            minWidth: 100,
        },
        {
            id: ColumnName.size,
            compare: (a, b) => {
                return (a.size || 0) - (b.size || 0);
            },
            header: 'Size',
            renderCell: (doc) => {
                return (
                        <Text>{utils.formatSize(doc.size as number)}</Text>
                );
            },
            defaultWidth: 120,
            minWidth: 100,
        },
    ];

    const InfoBundle = bundleIcon(InfoFilled, InfoRegular);
    const ArrowDownloadBundle = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular);
    const DeleteBundle = bundleIcon(DeleteFilled, DeleteRegular);
    const RenameBundle = bundleIcon(RenameFilled, RenameRegular);

    const actions: CustomDataTableAction<CaseDocument>[] = [
        {
            id: 'details',
            ariaLabel: 'Details',
            canAccess: (doc) => access.cases.canEditDocuments(doc?.caseId) || false,
            icon: <InfoBundle />,
            label: 'Details',
            tooltip: 'Details',
            onClick: () => {
                !!setDrawerOpen && setDrawerOpen(true);
            },
            singleSelectItemOnClick: true,
        },
        {
            id: 'download',
            ariaLabel: 'Download',
            canAccess: (doc) => access.cases.canDownloadDocuments(doc?.caseId) || false,
            icon: <ArrowDownloadBundle />,
            label: 'Download',
            tooltip: 'Download',
            onClick: (doc) => {
                downloadDocAsync(doc);
            },
            onMenuClick: (doc) => {
                downloadDocAsync(doc);
            },
        },
        {
            id: 'rename',
            ariaLabel: 'Rename',
            canAccess: (doc) => access.cases.canEditDocuments(doc?.caseId) || false,
            icon: <RenameBundle />,
            label: 'Rename',
            tooltip: 'Rename',
            singleSelectItemOnClick: true,
            onMenuClick: (doc) => {
                setActionDoc(doc);
                setRenameOpen(true);
            },
            onClick: (doc) => {
                setActionDoc(doc);
                setRenameOpen(true);
            },
            // renderDialog: (doc, children) => {
            //     return (
            //         <RenameDocDialog doc={doc} onModifyDocument={onModifyDocument}>
            //             {children}
            //         </RenameDocDialog>
            //     );
            // },
        },
        {
            id: 'delete',
            ariaLabel: 'Delete',
            canAccess: (doc) => access.cases.canDeleteDocuments(doc?.caseId) || false,
            icon: <DeleteBundle />,
            label: 'Delete',
            tooltip: 'Delete',
            singleSelectItemOnClick: true,
            onMenuClick: (doc) => {
                setActionDoc(doc);
                setDelConfirmOpen(true);
            },
            renderDialog: (doc, children) => {
                return (
                    <ConfirmationDialog
                        title='Confirm Delete'
                        icon={<DeleteRegular />}
                        text={`Are you sure you want to delete document ${doc?.name}?`}
                        onConfirm={() => clickDeleteDocs([doc])}
                    >
                        {children}
                    </ConfirmationDialog>
                );
            },
        },
    ];

    const onConfirmDelete = (e: any) => {
        e.stopPropagation();

        if (actionDoc) {
            clickDeleteDocs([actionDoc]);
        }
    };

    return (
        <>
            <CustomDataTable<CaseDocument>
                items={docs || []}
                rowId='docId'
                singleSelect={singleSelect}
                onSelectionChanged={onSelectionChanged}
                columns={columns}
                actions={actions}
                {...otherProps}
            />
            <RenameDocDialog
                doc={actionDoc}
                onModifyDocument={onModifyDocument}
                open={renameOpen}
                onOpenChange={(isOpen) => {
                    setRenameOpen(isOpen as boolean);
                    setActionDoc(undefined);
                    !isOpen && setDrawerOpen && setDrawerOpen(false);
                }}
            />
            <ConfirmationDialog
                title='Confirm Delete'
                icon={<DeleteRegular />}
                text={`Are you sure you want to delete document ${actionDoc?.name}?`}
                open={delConfirmOpen}
                onOpenChange={(isOpen) => {
                    setDelConfirmOpen(isOpen as boolean);
                    setActionDoc(undefined);
                    !isOpen && setDrawerOpen && setDrawerOpen(false);
                }}
                onConfirm={onConfirmDelete}
            />
        </>
    );
};
